import type { ReactNode } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Warning as WarningIcon } from '@mui/icons-material';

interface EfemSetupSectionProps {
  title: string;
  statusLabel?: string;
  statusVariant?: 'success' | 'warning';
  headerAction?: ReactNode;
  subtitle?: string;
  disabled?: boolean;
  disabledHint?: string;
  children: ReactNode;
}

export function EfemSetupSection({
  title,
  statusLabel,
  statusVariant = 'success',
  headerAction,
  subtitle,
  disabled = false,
  disabledHint,
  children,
}: EfemSetupSectionProps) {
  if (disabled) {
    return (
      <Box>
        <Typography variant="subtitle2" fontWeight={600} color="text.disabled" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.disabled" fontStyle="italic">
          {disabledHint ?? 'Complete the step above first.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={subtitle ? 0.5 : 1} flexWrap="wrap">
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
        {statusLabel && (
          <Chip
            icon={statusVariant === 'success' ? <CheckCircleIcon /> : <WarningIcon />}
            label={statusLabel}
            size="small"
            color={statusVariant}
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        )}
        {headerAction && <Box sx={{ ml: 'auto' }}>{headerAction}</Box>}
      </Box>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </Box>
  );
}
